/**
 * SFrame101 – Situation Assessment (Step 1 of 3)
 * Figma Frame 1 (node 1018:13458)
 *
 * "We're here to help you handle this."
 * "Which best describes your situation?"
 *  - I already have the last will & testament
 *  - I don't have the will but I have the death certificate
 *  - I don't have the will or the death certificate
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandSymbol from '../ui/BrandSymbol';
import radioChecked from '../../assets/images/radio-checked.svg';
import radioUnchecked from '../../assets/images/radio-unchecked.svg';

type SituationType = 'has-will' | 'has-death-cert' | 'neither' | null;

const options: { id: SituationType; label: string }[] = [
  { id: 'has-will', label: 'I already have the last will & testament' },
  { id: 'has-death-cert', label: "I don't have the will but I have the death certificate" },
  { id: 'neither', label: "I don't have the will or the death certificate" },
];

export default function SFrame101() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<SituationType>(null);

  const handleNext = () => {
    if (!selected) return;
    navigate('/sframe-105', { state: { situationType: selected } });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Brand logo */}
      <div className="flex justify-center pt-6">
        <BrandSymbol size="lg" showText />
      </div>

      {/* Main content */}
      <div className="flex-1 px-6 pt-12 pb-32 max-w-2xl mx-auto w-full">
        <h1 className="font-display text-2xl sm:text-3xl text-blue-900 mb-12">
          We're here to help you handle this.
        </h1>

        <p className="text-base font-medium text-black mb-6">
          Which best describes your situation?
        </p>

        {/* Radio options */}
        <div className="space-y-4">
          {options.map((option) => (
            <label
              key={option.id}
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => setSelected(option.id)}
            >
              <img
                src={selected === option.id ? radioChecked : radioUnchecked}
                alt=""
                className="w-[18px] h-[18px] shrink-0"
              />
              <span className="text-base text-black font-normal">
                {option.id === 'has-will' ? (
                  <>I already have the <span className="underline">last will & testament</span></>
                ) : (
                  option.label
                )}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Bottom progress bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-blue-900 text-white-alt px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <p className="font-semibold text-base">Welcome</p>
            <p className="text-sm text-blue-100 opacity-80">Step 1/3</p>
          </div>
          <button
            onClick={handleNext}
            disabled={!selected}
            className={`flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium transition-all ${
              selected
                ? 'bg-white-alt/10 hover:bg-white-alt/20 text-white-alt cursor-pointer'
                : 'opacity-40 cursor-not-allowed text-white-alt/60'
            }`}
          >
            Next
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        {/* Progress track */}
        <div className="max-w-3xl mx-auto mt-2">
          <div className="w-full bg-white-alt/20 rounded-full h-1">
            <div className="bg-green-600 h-1 rounded-full transition-all duration-300" style={{ width: '33%' }} />
          </div>
        </div>
      </div>
    </div>
  );
}
